import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { X, UserPlus } from "lucide-react";

const SERVICES = [
  "Home Cleaning",
  "Office Cleaning",
  "Commercial Cleaning",
  "Construction Cleanup",
];

export default function AddClientModal({ open, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const empty = {
    name: "",
    phone: "",
    email: "",
    service_type: SERVICES[0],
    address: "",
    preferred_date: today,
    preferred_time: "",
    message: "Recurring client (added by admin)",
    estimated_value: 0,
    is_recurring: true,
  };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  if (!open) return null;
  const upd = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        estimated_value: Number(form.estimated_value) || 0,
      });
      // Auto-accept admin-added recurring clients
      if (form.is_recurring) {
        await api.patch(`/bookings/${data.id}`, { status: "accepted" });
      }
      toast.success("Client added");
      setForm(empty);
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error("Couldn't add client");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="add-client-modal" className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4">
      <div className="relative w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-black/10 p-6">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--pc-green)] text-black">
              <UserPlus size={18} />
            </span>
            <div>
              <div className="eyebrow text-black/40">New Entry</div>
              <h3 className="font-display text-xl font-bold tracking-tight">Add Client</h3>
            </div>
          </div>
          <button
            data-testid="add-client-close"
            onClick={onClose}
            className="rounded-full border border-black/15 p-2 hover:border-black"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <F label="Full Name *">
              <input data-testid="ac-name" required value={form.name} onChange={upd("name")} className="acp" />
            </F>
            <F label="Phone *">
              <input data-testid="ac-phone" required value={form.phone} onChange={upd("phone")} className="acp" />
            </F>
            <F label="Email *">
              <input data-testid="ac-email" required type="email" value={form.email} onChange={upd("email")} className="acp" />
            </F>
            <F label="Service *">
              <select data-testid="ac-service" value={form.service_type} onChange={upd("service_type")} className="acp">
                {SERVICES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </F>
            <F label="Address *" className="md:col-span-2">
              <input data-testid="ac-address" required value={form.address} onChange={upd("address")} className="acp" />
            </F>
            <F label="Next Service Date">
              <input data-testid="ac-date" type="date" value={form.preferred_date} onChange={upd("preferred_date")} className="acp" />
            </F>
            <F label="Time">
              <input data-testid="ac-time" type="time" value={form.preferred_time} onChange={upd("preferred_time")} className="acp" />
            </F>
            <F label="Quote / Rate (CAD)">
              <input data-testid="ac-quote" type="number" min="0" step="1" value={form.estimated_value} onChange={upd("estimated_value")} className="acp" />
            </F>
            <F label="Frequency / Notes">
              <input data-testid="ac-notes" value={form.message} onChange={upd("message")} className="acp" placeholder="Weekly · Bi-weekly · Monthly" />
            </F>
          </div>

          <label className="mt-5 flex cursor-pointer items-center gap-3 rounded-xl border border-[var(--pc-green)] bg-[var(--pc-green-light)] p-4">
            <input
              data-testid="ac-recurring"
              type="checkbox"
              checked={form.is_recurring}
              onChange={upd("is_recurring")}
              className="h-4 w-4 accent-[var(--pc-green-hover)]"
            />
            <span className="text-sm font-semibold">
              Recurring client (auto-accepted, marked as ongoing)
            </span>
          </label>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              data-testid="ac-cancel"
              onClick={onClose}
              className="rounded-full border border-black/15 px-5 py-2.5 text-sm font-semibold hover:border-black"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="ac-submit"
              disabled={loading}
              className="pc-btn-primary text-sm disabled:opacity-60"
            >
              {loading ? "Saving…" : "Add Client"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .acp {
          width:100%; border:1px solid rgba(10,10,10,0.15); background:#fff;
          padding:.7rem .9rem; border-radius:.75rem; font-size:.9rem; font-family:inherit;
        }
        .acp:focus { outline:none; border-color:var(--pc-green); box-shadow:0 0 0 4px rgba(0,230,118,0.18); }
      `}</style>
    </div>
  );
}

function F({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-black/60">
        {label}
      </span>
      {children}
    </label>
  );
}
